<script setup>
import ModalBase from "@/components/modal/ModalBase.vue";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import ArgonLoadingButton from "@/components/ArgonLoadingButton.vue";
import ArgonSelect from "@/components/ArgonSelect.vue";
import { ref, onMounted, watch } from "vue";
import { webhookStore } from "@/_store/webhookStore";
import { useVuelidate } from "@vuelidate/core";
import { required, helpers, url } from "@vuelidate/validators";
import { useStore } from "vuex";
import { errorUtils } from "@/utils";


const emits = defineEmits(["close", "success"]);
const modalRef = ref(null);
const disableButton = ref(false);
const snackbar = ref(null);
const data = ref({ name: "", uri:"", event_type: "", auth_header_key: "", auth_header_value: "" });

const rules = {
  name: {
    required: helpers.withMessage("Nombre es requerido", required),
  },
  uri:{
    required: helpers.withMessage("URL es requerido", required),
    url: helpers.withMessage("Formato URL incorrecto", url)
  },
  event_type:{
    required: helpers.withMessage("Tema es requerido", required)
  }
};
const props = defineProps({
  id: {
    type: String,
    default: ""
  }
});
const v$ = useVuelidate(rules, data);
const whStore = webhookStore();
const store = useStore();
const  eventOptions = [
  { value: "STOP", label: "Parada" },
  { value: "LAST_MILE", label: "Milla Intermedia" },
];

onMounted(() => {
  if(props.id){
    whStore.getWebhook(props.id)
  }
});

function closeSnackbar() {
  snackbar.value = null;
}
function closeModal() {
  emits("close");
  modalRef.value?.closeModal();
}
function  saveWebhook() {
  const promise = validate();
  let message = "";
  Promise.all([promise]).then((values) => {
    for (const value of values) {
      for (const error of value) {
        message = `${message}<li>${error.$message}</li>`;
      }
    }
    if (message != "") {
      snackbar.value = {
        title: "Error",
        message: `<ul>${message}</ul>`,
        type: "danger",
        color: "white",
      };
      return;
    }
    disableButton.value = true;
    if(props.id && props.id!=''){
      whStore.editWebhook(data.value);
      return
    }
    whStore.newWebhook(data.value);
  });
}
async function validate() {
  await v$.value.$validate();
  return v$.value.$errors;
}

watch(() => whStore.created, (value) => {
  if (value) {
    emits("success", value);
    closeModal();
    snackbar.value = {
      title: "OK",
      message: "Solicitud enviada",
      type: "info",
      color: "white",
    };
    disableButton.value = false;
  }
});
watch(() => whStore.updated, (value) => {
  if (value) {
    emits("success", value);
    closeModal();
    snackbar.value = {
      title: "OK",
      message: "Solicitud enviada",
      type: "info",
      color: "white",
    };
    disableButton.value = false;
  }
});

watch(() => whStore.error, (err) => {
  if (err) {
    snackbar.value = {
      title: "Error",
      message: errorUtils.getErrorMessageHtml(err),
      type: "danger",
      color: "white",
    };
    disableButton.value = false;
  }
});
watch(() => whStore.webhook, (value) => {
  if (value) {
    data.value = value;
  }
});

onMounted(() => {
  const bootstrap = store.state.bootstrap;
  const modalInstance = new bootstrap.Modal(document.getElementById("modalWebhook"), {
    keyboard: true,
  });
  modalInstance.show();
});
</script>


<template>
  <modal-base
    id="modalWebhook"
    ref="modalRef"
    width="48rem"
    min-height="15rem"
    @hide="closeModal"
  >
    <template #header>
      <h6 class="modal-title"> {{id? 'Modificar' : 'Nuevo' }}</h6>
    </template>

    <template #body>
      <div class="row">
        <div class="col-2">
          <label
            for="col-form-label"
            class="col-form-label required"
            >Nombre
          </label>
        </div>
        <div class="col-10">
            <argon-input
            v-model="data.name"
            placeholder=""
            :max-length="50"
            :error="v$.name.$error"
            />
        </div>
      </div>
      <div class="row">
        <div class="col-2">
          <label          
            for="col-form-label"
            class="col-form-label required"
            >URL              
          </label>
        </div>
        <div class="col-10">
            <argon-input
            v-model="data.uri"
            placeholder=""
            :max-length="500"
            :error="v$.uri.$error"
            />
        </div>
      </div>
      <div class="row">
        <div class="col-2">
          <label
            for="col-form-label"
            class="col-form-label required"
            >Tema
          </label>
        </div>
        <div class="col-10">
            <argon-select
              id="event_type"
              v-model="data.event_type"
              placeholder="" name="event_type"
              :error="v$.event_type.$error"
              :options="eventOptions" filterable></argon-select>
        </div>
      </div>
      <div class="row">
        <div class="col-2">
          <label
            v-tooltip="'Clave que se agregará al header de la petición, que puedes usar para tu autorización'"
            for="col-form-label"
            class="col-form-label"
            >Auth key
            <sup                
                class="text-primary sup-question"
              >?</sup>
          </label>
        </div>
        <div class="col-10">
            <argon-input
            v-model="data.auth_header_key"
            :max-length="50"
            placeholder=""
            />
        </div>
      </div>
      <div class="row">
        <div class="col-2">
          <label
            v-tooltip="'Valor de la clave anterior, que puedes usar para tu autorización'"
            for="col-form-label"
            class="col-form-label"
            >Auth Value
            <sup                
                class="text-primary sup-question"
              >?</sup>
          </label>
        </div>
        <div class="col-10">
            <argon-input
            v-model="data.auth_header_value"
            :max-length="500"
            placeholder=""
            />
        </div>
      </div>
    </template>

    <template #footer>
      <argon-loading-button
        class="btn bg-gradient-primary btn-xs"
        color="primary"
        :label="disableButton ? 'Guardando...' : 'Guardar'"
        :loading="disableButton"
        :disable-button="disableButton"
        @click=" saveWebhook"
      />
    </template>

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </modal-base>
</template>
